import '../styles/global.less'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { register, unregister } from '@yogesh/next-offline/runtime'
import { AnimatePresence, motion } from 'framer-motion'
import { cache } from 'swr'
import * as Sentry from '../lib/sentry'
import { fetcher } from '../lib/hooks'
import analytics from '../lib/analytics'

import Layout from '../components/Layout'

Sentry.init()
Sentry.setUser()

const PUBLIC_PATHS = ['/', '/forgot-password']

const pageVariants = {
  pageInitial: {
    backgroundColor: 'white',
    opacity: 0,
  },
  pageAnimate: {
    backgroundColor: 'transparent',
    opacity: 1,
  },
  pageExit: {
    backgroundColor: 'white',
    opacity: 0,
  },
}

const pageMotionProps = {
  initial: 'pageInitial',
  animate: 'pageAnimate',
  exit: 'pageExit',
  variants: pageVariants,
}

export default function MyApp({ Component, pageProps, err }) {
  const router = useRouter()
  const privateRoute = !PUBLIC_PATHS.includes(router.pathname)
  const auth = JSON.parse(
    (process.browser && localStorage.getItem('auth')) || '{}',
  )
  const { user } = auth
  const { title } = pageProps || {}
  useEffect(() => {
    if (user) {
      analytics.identify(user.id, {
        name: user.name,
        email: user.emailid,
        mobile: user.mobile,
        user_role: user.su[0].rolename,
        school_id: user.su[0].schoolid,
        school_name: user.su[0].schoolname,
      })
    }

    analytics.page(title || 'Not Found')
    register('/service-worker.js', { scope: '/' })

    return () => unregister()
  }, [])

  useEffect(() => {
    if (user) {
      fetcher('/featureAccess').then(({ success, ratio_met: ratioMet }) => {
        if (success) localStorage.setItem(`tab_student_ratio_met`, ratioMet)
      })
    }
  }, [user])

  useEffect(() => {
    const handleRouteChange = () => {
      analytics.page(title || 'Not Found')
      // FOR NOW: dirty fix for scroll to top on navigation
      document.getElementById('content-main')?.scrollTo(0, 0)
    }

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events, title])

  useEffect(() => {
    if (!user && privateRoute) router.replace('/')
    else if (user) {
      const maspInfo = JSON.parse(localStorage.getItem('masp'))
      if (!maspInfo) {
        fetcher('/schoolOfferings').then(({ success, data }) => {
          if (success) {
            // if all school types are platform only, then it is masp
            const isMasp =
              data.filter((s) => !s.schoolType.includes('Platform'))?.length ===
              0
            localStorage.setItem(
              'masp',
              JSON.stringify({ isMasp, menuOpen: isMasp }),
            )
            if (isMasp) {
              router.reload()
            }
          }
        })
      }

      const noUserAcadYearInfo =
        Object.keys(auth).indexOf('userAcademicYearIds') === -1

      if (noUserAcadYearInfo) {
        // auto logout
        analytics.track('account_logged_out', {
          invoked_from_screen: router.route,
          logout_confirmation: true,
        })
        localStorage.removeItem('auth')
        localStorage.removeItem('masp')
        localStorage.removeItem('selectedAY')
        analytics.reset()
        Sentry.setUser(null)
        cache.clear()
        router.replace('/')
      }

      const noClasses =
        !auth.userAcademicYearIds || auth.userAcademicYearIds === ''

      if (noClasses) {
        if (router.route !== '/no-classes' && router.route !== '/academy')
          router.replace('/no-classes')
      } else if (
        !localStorage.getItem(`onboarded_${user.id}`) &&
        router.route !== '/onboarding'
      )
        router.replace('/onboarding')
      else if (!privateRoute) router.replace('/classes')
    }
  }, [router, privateRoute, user])

  const { header = true, logo = false, back = '', footer = true } = pageProps

  return (
    <Layout
      header={header}
      logo={logo}
      title={title}
      route={router.route}
      back={back}
      footer={footer}
      show={
        privateRoute &&
        router.pathname !== '/onboarding' &&
        router.pathname !== '/no-classes'
      }
    >
      <AnimatePresence exitBeforeEnter>
        <motion.div key={router.route} {...pageMotionProps}>
          <Component {...pageProps} err={err} key={router.route} />
        </motion.div>
      </AnimatePresence>
    </Layout>
  )
}
